import { authFetch } from 'core/client';

import { refresher } from 'core/jwt';
import { clientStore } from 'apps/client/app';

import uuid from 'uuid/v4';

export class GltfConnection {
  constructor(viewerControl, fileId) {
    this.viewerControl = viewerControl;

    this.module = this.viewerControl.scriptPathLocator;

    // parseHead state
    this.minHeadChunksLength = 100;
    this.headChunks = [];
    this.headLen = -1;
    this.urlLen = -1;
    this.gltfLen = -1;

    // assemble gltf endpoint using a random uuid and the fileId
    const gltfUuid = uuid();
    const gltfUrl = `${GLTF_URL_BASE}/gltf/${gltfUuid}/${fileId}/gltf`;

    // we start fetching the gltf at construction time using the streams api so that
    // it can download while the wasm runtime is initializing, and is ready for processing
    // in the wasm sooner
    // we also want to start fetching the databuffer as soon as possible, so the url for
    // the databuffer is sent first, this is made possible by prepending the gltf json
    // with a header containing integer lengths, after which the databuffer url string
    // follows, after which the gltf json follows:
    // wf15;371;415065https://s3/uploads/space/data.bin{"accessors":...
    // wf headLen urlLen gltfLen packedUrl gltf
    this.gltfPromise = this.retryFetchErrors(authFetch, gltfUrl, {
      credentials: 'same-origin',
      signedInProfile: clientStore.signedInProfile,
      HMAC: clientStore.HMAC,
    })
      .catch(err => {
        this.viewerControl.LogFail(err);
      })
      .then(response => {
        if (!response?.body) {
          return undefined;
        }

        // reader is a stream, we create a new stream so that we can receive individual
        // chunks through the reader we got from fetch, parse and cut off the header from
        // the chunks and only pass the gltf json through the second stream we are creating
        // here
        const { body } = response;
        const reader = body.getReader();
        return new ReadableStream({
          start: async controller => {
            let headDone = false;
            while (true) {
              // eslint-disable-next-line no-await-in-loop
              const { done, value } = await reader.read();
              // when no more data needs to be consumed, break the reading
              // when done is true, value is undefined
              if (done) {
                break;
              }

              // parseHead will try to read the wf header including the url,
              // for example wf15;371;415065https://...
              // - it will set headLen, urlLen and gltfLen with length parsed
              // from the header
              // - it will initiate the databuffer fetch and set dataPromise to
              // the databuffer fetch promise or an immediatly resolving promise
              // if no header was found
              // - it will return headDone indicating the header was parsed and
              // we can continue passing gltfChunks to the receiver of this stream
              // - gltfChunk is returned because parseHead will cut off the header
              // from the fetched stream so that we can only enqueue the gltf
              // string without header
              let gltfChunk = value;
              if (!headDone) {
                ({ headDone, gltfChunk } = this.parseHead(value));
              }
              if (headDone) {
                this.headChunks = [];
                controller.enqueue(gltfChunk);
              }
            }

            // Close the stream
            controller.close();
            reader.releaseLock();
          },
        });
      });

    // dataPromise will either resolve to a fetch response (by calling
    // resolveDataPromise(fetch(url)) in parseHead), or reject if we are
    // viewing an old conversion and no packedUrl can be parsed from the
    // header
    this.dataPromise = new Promise(resolve => {
      this.resolveDataPromise = packedUrl => {
        if (!packedUrl) {
          resolve(undefined);
        }

        fetch(packedUrl, { credentials: 'same-origin' })
          .then(response => {
            if (!response.ok) {
              throw Error(response.statusText);
            }
            return response;
          })
          .catch(err => this.viewerControl.LogFail(err))
          .then(response => {
            if (!response?.body) {
              resolve(undefined);
              return;
            }
            const { body } = response;
            const reader = body.getReader();
            const stream = new ReadableStream({
              start: async controller => {
                while (true) {
                  // eslint-disable-next-line no-await-in-loop
                  const { done, value } = await reader.read();
                  if (done) {
                    break;
                  }
                  controller.enqueue(value);
                }
                controller.close();
                reader.releaseLock();
              },
            });
            resolve(stream);
          });
      };
    });
  }

  // I copy pasted this function from here:
  // https://stackoverflow.com/questions/38213668/promise-retry-design-patterns
  // and tweaked it to be specific for this use case (which is calling fetch)
  retryFetchErrors = (fn, url, options, retries = 2) => {
    if (!retries) {
      if (!clientStore.signedInProfile) {
        this.viewerControl?.RequestSignIn();
      }
      return Promise.reject(new Error('unauthorized'));
    }
    return fn(url, options)
      .then(async response => {
        if (!response.ok) {
          // check 401 error and refresh token again
          if (response.status === 401) {
            await refresher.refreshToken();
            throw Error('refresh');
          }

          throw Error(response.statusText);
        }
        return response;
      })
      .catch(fetchErr => {
        if (fetchErr.message === 'refresh') {
          // keep trying...
          return this.retryFetchErrors(fn, url, options, retries - 1);
        }
        return Promise.reject(fetchErr);
      });
  };

  // requestGltf and requestData are supposed to be called _only_ by the
  // wasm code, they will resolve the promises we constructed when instatiating
  // this GltfConnection and pass the received gltf/data through functions
  // exposed by the wasm module (receiveGltf and receiveData, see Connection.cpp
  // for the implementation of those)
  // the id is used to associate the request with some state I need to store on
  // the wasm side, technically this is not needed anymore with only one dataBuffer,
  // but in cases where we have multiple concurrent fetches I need the id to
  // associate the receive callback with the request I made
  requestGltf = async id => {
    // gltfPromise returns a stream which we can read and receive gltf
    // chunks
    const gltfStream = await this.gltfPromise;
    if (!gltfStream) {
      return;
    }

    const gltfReader = gltfStream.getReader();
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await gltfReader.read();
      // done is passed to the receive functions so we can free the data we accumulated
      // inside the wasm once the download is done, so this needs to be called before
      // the break, even though we pass value=undefined when done=true
      try {
        if (
          this.viewerControl.gltfConnections.has(this.viewerControl.file?.id)
        ) {
          this.module.receiveGltf(id, value, done);
        }
      } catch (e) {
        this.viewerControl.LogException(e);
      }
      if (done) {
        break;
      }
    }
    gltfReader.releaseLock();
  };

  // similar like requestGltf but for data
  requestData = async (id, length) => {
    // dataPromise either resolve and returns a fetch response for the packedUrl
    const dataStream = await this.dataPromise;
    if (!dataStream) {
      return;
    }

    const dataReader = dataStream.getReader();
    let received = 0;
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await dataReader.read();
      try {
        if (
          this.viewerControl.gltfConnections.has(this.viewerControl.file?.id)
        ) {
          this.module.receiveData(id, value, done);
        }
      } catch (e) {
        this.viewerControl.LogException(e);
      }

      if (done) {
        this.viewerControl.downloadPerc = 100;
        break;
      }

      received += value.length;
      const perc = Math.ceil((100 / length) * received);
      const diff = perc - this.viewerControl.downloadPerc;
      if (diff >= 10 || perc >= 100 || done) {
        this.viewerControl.downloadPerc = perc;
      }
    }
    dataReader.releaseLock();

    // this code could be used to not use streaming at all here and just pass the data as
    // one big chunk, this is actually a little faster then using streaming
    // response.arrayBuffer()
    //   .then(array => {
    //     const data = new Uint8Array(array);
    //     this.module.receiveData(id, data, false);
    //     this.module.receiveData(id, undefined, true);
    //   });
  };

  parseHead = value => {
    // I define minHeadChunksLength as the minimum amount of bytes
    // that needs to have been fetched before I attempt to parse
    // the header
    let headChunksLength = 0;
    for (let i = 0; i < this.headChunks.length; i += 1) {
      headChunksLength += this.headChunks[i].length;
    }
    if (headChunksLength + value.length < this.minHeadChunksLength) {
      this.headChunks.push(value);
      return { headDone: false, gltfChunk: value };
    }

    // we add received chunks into headChunks until we have enough to parse
    // the header+url
    // if nothing is in headChunks, then chunk = value, if there is something
    // in headChunks then we add value, then concatenate all of them together
    // and assign the resulting buffer to chunk
    let chunk = value;
    if (this.headChunks.length > 0) {
      this.headChunks.push(value);
      chunk = Uint8Array.from(
        Array.prototype.concat(...this.headChunks.map(a => Array.from(a))),
      );
    }

    // wf14;321;123567<url321><gltf123567>

    // if headLen < 0 then we have never parsed the header and need to do still
    if (this.headLen < 0) {
      // wf == [119 102]
      // check if the first two bytes of the chunk equal "wf", which indicates
      // the start of the header string
      if (chunk[0] !== 119 || chunk[1] !== 102) {
        if (this.resolveDataPromise) {
          this.resolveDataPromise(undefined);
        }
        return { headDone: true, gltfChunk: chunk };
      }

      // process chunk from 2 (after the starting wf) to chunk.length (aborting
      // early as soon as we have read headLen and urlLen)
      let tmp = '';
      for (let i = 2; i < chunk.length; i += 1) {
        // abort condition, if we parsed a headLen, and advanced beyond it,
        // we are done parsing the header
        if (this.headLen >= 0 && i >= this.headLen) {
          break;
        }
        // safety to not endlessly loop
        if (i > 100) {
          break;
        }

        // accumulate characters into tmp string, as soon as we see a ;
        // we use parseInt to convert the accumulated string to an integer,
        // we then just assume a certain order (headLen first, then urlLen)
        // to assign headLen and urlLen
        // ; == 59
        if (chunk[i] !== 59) {
          tmp += String.fromCharCode(chunk[i]);
        } else if (this.headLen < 0) {
          this.headLen = parseInt(tmp, 10);
          tmp = '';
        } else if (this.urlLen < 0) {
          this.urlLen = parseInt(tmp, 10);
          tmp = '';
        }
      }

      // loop above aborts and leaves tmp filled with the characters read
      // after the last ; which is then parsed as gltfLen
      if (tmp !== '') {
        this.gltfLen = parseInt(tmp, 10);
      }

      // sanity check, at this point all of these should be > 0
      if (!(this.headLen > 0 && this.urlLen > 0 && this.gltfLen > 0)) {
        this.viewerControl.LogConversionProblem(
          new Error('error parsing wf header, aborting'),
        );

        if (this.resolveDataPromise) {
          this.resolveDataPromise(undefined);
        }
        return { headDone: true, gltfChunk: chunk };
      }
    }

    // technically its possible we don't yet have enough data to parse the url
    this.gltfStart = this.headLen + this.urlLen;
    if (chunk.length < this.gltfStart) {
      return { headDone: false, gltfChunk: chunk };
    }

    // get the string from headLen to headLen+urlLen and use it as packedUrl to
    // start fetching the databuffer
    const urlStart = this.headLen;
    const urlEnd = this.headLen + this.urlLen;

    let packedUrl = '';
    for (let i = urlStart; i < urlEnd; i += 1) {
      packedUrl += String.fromCharCode(chunk[i]);
    }

    this.resolveDataPromise(packedUrl);

    // return everything in chunk after gltfStart (which is where the url ends)
    // as gltfChunk so that the calling loop will enqueue it into the gltf stream
    return {
      headDone: true,
      gltfChunk: chunk.subarray(this.gltfStart, chunk.length),
    };
  };
}
